import { inject, Injectable } from '@angular/core';
import { AnalyticsService, trackTimedApiCall } from '../../integrations/analytics';
import { ShipmentFlowStateService } from '../../services/shipment-flow-state.service';
import { AXIOS_INSTANCE } from '../api-client.tokens';
import type {
  PackageSelectionItem,
  PackageSelectionRequest,
} from './models/package-selection.model';
import type {
  ProductSelectionItem,
  ProductSelectionRequest,
} from './models/product-selection.model';

/**
 * Product Master API client for package catalog and related lookups.
 */
@Injectable({ providedIn: 'root' })
export class ProductMasterApiService {
  private readonly http = inject(AXIOS_INSTANCE);
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly analytics = inject(AnalyticsService);

  /**
   * Returns the package selection catalog for the Package step.
   * Posts the SA attestation token and the wizard flow id so Product Master
   * can verify prior steps and mint per-item package-selection JWTs.
   */
  getPackageSelection(request: PackageSelectionRequest): Promise<PackageSelectionItem[]> {
    const flowId = this.shipmentFlowState.ensureFlowId();

    return trackTimedApiCall(this.analytics, 'getPackages', () =>
      this.http
        .post<PackageSelectionItem[]>('/products/package/selection', request, {
          headers: { 'x-flow-id': flowId },
        })
        .then((response) => response.data),
    );
  }

  /**
   * Returns products for an attested package, including service-time bands and per-item JWTs.
   * Posts package-selection + SA tokens with the wizard flow id so Product Master can verify
   * prior steps and mint `type: product-selection` attestations.
   */
  getProductSelection(request: ProductSelectionRequest): Promise<ProductSelectionItem[]> {
    const flowId = this.shipmentFlowState.ensureFlowId();

    return trackTimedApiCall(this.analytics, 'getServiceOptions', () =>
      this.http
        .post<ProductSelectionItem[]>('/products/product/selection', request, {
          headers: { 'x-flow-id': flowId },
        })
        .then((response) => response.data),
    );
  }
}
